import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import furnitureImg from "@/assets/providedassets/builtfurniture.jpg";
import paintImg from "@/assets/providedassets/textured paint.jpg";
import tileImg from "@/assets/providedassets/builttiles.jpg";
import interiorImg from "@/assets/interior-inspiration.jpg";
import decorImg from "@/assets/providedassets/Home decor.jpg";
import realEstateImg from "@/assets/providedassets/real state.jpeg";

const INDUSTRIES = [
  {
    id: "furniture",
    name: "Furniture",
    tagline: "Turn browsers into buyers",
    body: "Customers place sofas, beds, wardrobes, and décor into their own rooms before purchasing.",
    img: furnitureImg,
  },
  {
    id: "paint",
    name: "Paint",
    tagline: "The perfect color, every time",
    body: "Preview paint colors on real walls under realistic lighting conditions.",
    img: paintImg,
  },
  {
    id: "tiles",
    name: "Tiles & Flooring",
    tagline: "No more guesswork",
    body: "Compare flooring materials, wall tiles, textures, and finishes in real spaces.",
    img: tileImg,
  },
  {
    id: "interior",
    name: "Interior Design",
    tagline: "Client concepts in minutes",
    body: "Generate multiple design concepts instantly — no hours of manual rendering.",
    img: interiorImg,
  },
  {
    id: "decor",
    name: "Home Décor",
    tagline: "Inspire complete room designs",
    body: "Visualize lighting, rugs, curtains, and accessories in realistic room settings.",
    img: decorImg,
  },
  {
    id: "realestate",
    name: "Real Estate",
    tagline: "Sell before it's built",
    body: "Show furnished apartments and model homes with AI-powered virtual staging.",
    img: realEstateImg,
  },
];

export default function Industries() {
  const [activeId, setActiveId] = useState(INDUSTRIES[0].id);

  return (
    <section id="industries" className="bg-neutral-950 py-16 sm:py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest text-primary">Industries</p>
            <h2 className="mt-3 max-w-lg text-3xl font-bold text-white md:text-4xl">
              Built for every space that sells
            </h2>
          </div>
          <Link
            to="/solutions"
            className="group inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 transition-colors hover:text-primary"
          >
            Explore all solutions
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Mobile — static stacked cards, image on top with plain text below (no tap-to-expand) */}
        <div className="mt-10 flex flex-col gap-4 md:hidden">
          {INDUSTRIES.map((industry) => (
            <div
              key={industry.id}
              className="overflow-hidden rounded-2xl bg-white shadow-lg shadow-black/20"
            >
              <div className="relative aspect-[16/10] w-full">
                <img
                  src={industry.img}
                  alt={industry.name}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="p-5">
                <p className="text-xs font-extrabold uppercase tracking-widest text-neutral-900">
                  {industry.name}
                </p>
                <p className="mt-1.5 text-lg font-extrabold text-neutral-900">
                  {industry.tagline}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-500">
                  {industry.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop/tablet — expanding accordion gallery */}
        <div className="mt-12 hidden md:flex md:h-[480px] md:gap-3">
          {INDUSTRIES.map((industry) => {
            const isActive = industry.id === activeId;
            return (
              <button
                key={industry.id}
                type="button"
                onClick={() => setActiveId(industry.id)}
                onMouseEnter={() => setActiveId(industry.id)}
                onFocus={() => setActiveId(industry.id)}
                style={{ flexGrow: isActive ? 5 : 1, flexBasis: 0 }}
                className="group relative min-h-0 min-w-0 overflow-hidden rounded-2xl text-left transition-all duration-500 ease-in-out"
                aria-expanded={isActive}
              >
                <img
                  src={industry.img}
                  alt={industry.name}
                  className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 ${
                    isActive ? "scale-100" : "scale-105"
                  }`}
                />
                <div
                  className={`absolute inset-0 transition-colors duration-500 ${
                    isActive
                      ? "bg-gradient-to-t from-neutral-950/80 via-neutral-950/10 to-transparent"
                      : "bg-gradient-to-t from-primary/60 via-neutral-950/40 to-neutral-950/10 group-hover:from-primary/40 group-hover:via-neutral-950/25"
                  }`}
                />

                {/* Collapsed label */}
                <span
                  className={`absolute transition-opacity duration-300 ${
                    isActive ? "opacity-0" : "opacity-100"
                  } left-0 right-0 top-1/2 hidden -translate-y-1/2 px-3 text-center text-xl font-extrabold tracking-wide text-white drop-shadow-lg md:block`}
                >
                  {industry.name}
                </span>

                {/* Expanded caption */}
                <div
                  className={`absolute inset-x-0 bottom-0 p-6 transition-all duration-500 md:p-8 ${
                    isActive
                      ? "translate-y-0 opacity-100 delay-200"
                      : "translate-y-4 opacity-0"
                  }`}
                >
                  <p className="text-base font-extrabold uppercase tracking-widest text-primary drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]">
                    {industry.name}
                  </p>
                  <p className="mt-2 text-2xl font-extrabold text-white md:text-3xl">
                    {industry.tagline}
                  </p>
                  <p className="mt-2 max-w-sm text-base font-medium leading-relaxed text-white/70">
                    {industry.body}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
